import React from 'react'
import { useDispatch, useSelector, addNotification } from '@ggs/store'
import { useI18n } from '@ggs/i18n'
import { get, size } from 'lodash'
import { getDrupalSetting } from '@ggs/utils'
import { setDrupalToken } from '@ggs/store/actions/session'
import { useApiRequest } from './'

/**
 * @typedef {import("../../definitions").Jsonapi} Jsonapi
 */

/**
 * @typedef {() => Promise<string>} AcquireDrupalToken
 */

/**
 * @typedef {Object} DrupalApiHook
 * @property {AcquireDrupalToken} acquireDrupalToken
 */

/**
 * Hook that expose utilities to retrieve Drupal JSON API data.
 * @return {DrupalApiHook}
 */
function useDrupalApi() {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const baseUrl =
    getDrupalSetting('baseUrl') || `${process.env.REACT_APP_DRUPAL_BASE_URL}` || 'http://localhost'
  const basePath = 'jsonapi'
  const authToken = `${process.env.REACT_APP_DRUPAL_API_TOKEN}`
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'
  const drupalToken = useSelector((state) => state.session.drupalToken)
  const { doApiRequest } = useApiRequest({
    baseUrl,
    authToken,
    csrfToken: drupalToken,
  })

  /**
   * Negotiate to get a Drupal API CSRF token.
   * @return {Promise<string>}
   */
  const acquireDrupalToken = async () => {
    if (!isStorybook && !drupalToken) {
      const token = await getSessionToken()
      const rawToken = token.toString()
      if (size(token) > 10) {
        dispatch(setDrupalToken(token.toString()))
      }

      return rawToken
    }
  }

  /**
   * Check current user active Drupal session.
   */
  const checkActiveSession = async () => {
    const response = await doRequest(basePath)
    const userUUID = get(response, 'meta.links.me.href', null)

    return Boolean(userUUID)
  }

  /**
   * Process a Drupal API endpoint request.
   * @param {string} endpoint An API endpoint path
   * @param {any} data Payload data
   * @param {Object<string, string>=} customHeaders
   * @param {string} method
   * @return {Promise<Jsonapi>}
   */
  const doRequest = async (endpoint, data = null, customHeaders, method = '') => {
    const response = doApiRequest({
      endpoint,
      data,
      customHeaders,
      method,
    })

    const errorCode = get(response, 'errorCode', null)

    if (errorCode === 403) {
      const hasValidSession = await checkActiveSession()
      if (hasValidSession) {
        // Session is valid but user don't have privilege granted for the consumed endpoint.
        dispatch(
          addNotification(
            'drupal_api_request_failed',
            'error',
            t('Drupal API request was forbidden for current user.')
          )
        )
      } else {
        // Session is expired, user need to authenticate again, redirect to Drupal login.
        window.location.href = `${baseUrl}/user`
      }

      return null
    }

    return response
  }

  /**
   * Acquire a Drupal session token.
   */
  const getSessionToken = async () => {
    const endpoint = 'session/token'
    const response = await doRequest(endpoint)
    return response.data || ''
  }

  /**
   * Normalize resource item as entity object.
   * @param {Object<string, any>} item
   * @return {any}
   */
  const normalizeEntity = (item) => {
    return {
      ...item.attributes,
      type: item.type,
      id: item.id,
      relationships: item.relationships,
    }
  }

  return {
    acquireDrupalToken,
  }
}

export default useDrupalApi
